import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency, formatPercent } from "./i18n";

const COLORS = {
  primary: [30, 27, 75],    // indigo-950
  muted: [100, 116, 139],   // slate-500
  emerald: [4, 120, 87],
  amber: [180, 83, 9],
  rose: [190, 18, 60],
};

const diagColor = (d) =>
  d === "saine" ? COLORS.emerald : d === "surveiller" ? COLORS.amber : COLORS.rose;

export const exportPdf = ({ state, indicators, diagnostic, optim, recommendations, t, lang }) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 80, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("BudgetLens", margin, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(t.appTagline, margin, 60);
  doc.setFontSize(9);
  doc.text(new Date().toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US"), pageWidth - margin - 80, 40);

  let y = 110;
  doc.setTextColor(...COLORS.primary);

  // Client
  const c = state.client || {};
  const fullName = [c.prenom, c.nom].filter(Boolean).join(" ") || "—";
  doc.setFont("helvetica", "bold").setFontSize(14);
  doc.text(t.modules.client, margin, y);
  y += 8;
  doc.setDrawColor(...COLORS.muted);
  doc.line(margin, y, pageWidth - margin, y);
  y += 16;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(60, 60, 60);
  const clientLines = [
    `${t.client.prenom} / ${t.client.nom}: ${fullName}`,
    `${t.client.email}: ${c.email || "—"}`,
    `${t.client.telephone}: ${c.telephone || "—"}`,
    `${t.client.situationFamiliale}: ${c.situationFamiliale ? t.client.situations[c.situationFamiliale] : "—"}`,
    `${t.client.statutLogement}: ${c.statutLogement ? t.client.logements[c.statutLogement] : "—"}`,
    `${t.client.horizonPlacement}: ${c.horizonPlacement ? t.client.horizons[c.horizonPlacement] : "—"}`,
    `${t.client.objectifPatrimonial}: ${c.objectifPatrimonial || "—"}`,
  ];
  clientLines.forEach((line) => {
    doc.text(line, margin, y);
    y += 14;
  });
  y += 10;

  // Diagnostic
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(...COLORS.primary);
  doc.text(t.analyse.diagnostic, margin, y);
  y += 12;
  const dColor = diagColor(diagnostic.global);
  doc.setFillColor(...dColor);
  doc.roundedRect(margin, y, pageWidth - 2 * margin, 40, 6, 6, "F");
  doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(13);
  const diagLabel =
    diagnostic.global === "saine"
      ? t.analyse.diagnosticSaine
      : diagnostic.global === "surveiller"
      ? t.analyse.diagnosticSurveiller
      : t.analyse.diagnosticFragile;
  doc.text(diagLabel, margin + 16, y + 25);
  y += 60;

  // KPI table
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [[t.analyse.title, lang === "fr" ? "Valeur" : "Value"]],
    body: [
      [t.analyse.revenusTotaux, formatCurrency(indicators.revenus, lang)],
      [t.analyse.chargesFixes, formatCurrency(indicators.chargesFixes, lang)],
      [t.analyse.mensualitesCredits, formatCurrency(indicators.credits, lang)],
      [t.analyse.depensesVariables, formatCurrency(indicators.variables, lang)],
      [t.analyse.depensesTotales, formatCurrency(indicators.depensesTotales, lang)],
      [t.analyse.resteAVivre, formatCurrency(indicators.resteAVivre, lang)],
      [t.analyse.tauxEndettement, formatPercent(indicators.tauxEndettement, lang)],
      [t.analyse.poidsChargesFixes, formatPercent(indicators.poidsChargesFixes, lang)],
      [t.analyse.poidsVariables, formatPercent(indicators.poidsVariables, lang)],
      [t.analyse.mensualiteMax, formatCurrency(indicators.mensualiteMax, lang)],
      [t.analyse.mensualiteDispo, formatCurrency(indicators.mensualiteDispo, lang)],
      [t.analyse.capaciteEmprunt, formatCurrency(indicators.capaciteEmprunt, lang)],
    ],
    headStyles: { fillColor: COLORS.primary, textColor: 255, fontSize: 10 },
    bodyStyles: { fontSize: 10 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  y = doc.lastAutoTable.finalY + 20;

  if (y > 700) { doc.addPage(); y = 60; }

  // Optimisation
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(...COLORS.primary);
  doc.text(t.optimisation.title, margin, y);
  y += 10;
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [[t.optimisation.title, lang === "fr" ? "Valeur" : "Value"]],
    body: [
      [t.optimisation.tauxVariables, formatPercent(state.optimisation.tauxVariables, lang)],
      [t.optimisation.tauxFixes, formatPercent(state.optimisation.tauxFixes, lang)],
      [t.optimisation.economiesVariables, formatCurrency(optim.economiesVariables, lang)],
      [t.optimisation.economiesFixes, formatCurrency(optim.economiesFixes, lang)],
      [t.optimisation.gainMensuel, formatCurrency(optim.gainMensuel, lang)],
      [t.optimisation.nouveauRav, formatCurrency(optim.nouveauRav, lang)],
      [t.optimisation.epargneAnnuelle, formatCurrency(optim.epargneAnnuelle, lang)],
    ],
    headStyles: { fillColor: COLORS.primary, textColor: 255, fontSize: 10 },
    bodyStyles: { fontSize: 10 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });
  y = doc.lastAutoTable.finalY + 20;

  if (y > 700) { doc.addPage(); y = 60; }

  // Recommandations
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(...COLORS.primary);
  doc.text(t.recommandations.title, margin, y);
  y += 10;
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [[
      lang === "fr" ? "Thème" : "Topic",
      lang === "fr" ? "Type" : "Type",
      lang === "fr" ? "Message" : "Message",
    ]],
    body: recommendations.map((r) => [
      t.recommandations.labels[r.labelKey],
      r.level === "alerte"
        ? t.recommandations.alertes
        : r.level === "opportunite"
        ? t.recommandations.opportunites
        : t.recommandations.infos,
      t.recommandations.messages[r.msgKey],
    ]),
    headStyles: { fillColor: COLORS.primary, textColor: 255, fontSize: 10 },
    bodyStyles: { fontSize: 10 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...COLORS.muted);
    doc.text(
      `BudgetLens — ${t.appTagline}  |  ${i} / ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 20,
      { align: "center" }
    );
  }

  const filename = `BudgetLens_${fullName.replace(/\s+/g, "_") || "client"}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
};
